import { useState } from 'react';
import { HelpLink } from '../help/HelpLink';
import { Link } from 'react-router-dom';
import { categoriesText, de, statusLabel, viewLabel } from '@fsv/core';
import { Chip } from '../components/Controls';
import { Icon } from '../components/Icon';
import { PersonName } from '../components/Avatar';
import { MiniDrawing } from '../components/MiniDrawing';
import { ReasonDialog } from '../components/ReasonDialog';
import { StatusBadge } from '../components/Status';
import { useAircraftTypes, useInbox, useSettings, useWrite, useTenantPath, useDamageRef } from '../data/hooks';
import type { InboxEntry } from '@fsv/data';
import { resolveDrawing } from '../drawings/useDrawing';
import { tx } from '@fsv/data';
import { ErrorState, Loading } from './states';
import './InboxScreen.css';

function InboxCard({ entry, onReason }: { entry: InboxEntry; onReason: (mode: 'reject' | 'merge') => void }) {
  const ref = useDamageRef();
  const types = useAircraftTypes();
  const settings = useSettings();
  const write = useWrite();
  const to = useTenantPath();
  const d = entry.damage;
  const type = types.data?.find((t) => t.code === d.typeCode);
  const confirm = () =>
    write({
      run: (s) => s.reviewDamage(d.id, { action: 'confirm' }),
      success: tx.toast.statusChanged(statusLabel.bekannt),
      undo: (s, r) => s.revertReview(d.id, r.eventId),
    });
  return (
    <li className={`icard${entry.overdue ? ' icard--overdue' : ''}`}>
      <Link to={to(`/schaden/${d.id}`)} className="icard__link">
        <MiniDrawing
          className="icard__drawing"
          resolved={type ? resolveDrawing(type, d.view) : null}
          pins={[...entry.nearby.map((n) => ({ id: n.id, x: n.x, y: n.y, status: n.status })), { id: d.id, x: d.x, y: d.y, status: d.status, highlight: true }]}
          pinScale={1.3}
        />
        <div className="icard__body">
          <div className="row">
            <span className="mono icard__reg">{d.registration}</span>
            <span className="mono muted">{ref(d.number)}</span>
            <StatusBadge status={d.status} />
          </div>
          <h2 className="icard__title">{d.title}</h2>
          <p className="muted icard__meta">
            {categoriesText(d.categories)} · {viewLabel[d.view]} · <PersonName person={d.reportedBy} />
          </p>
          <div className="row icard__facts">
            <span className={`icard__age${entry.overdue ? ' is-overdue' : ''}`}>
              <Icon name="clock" size={16} />
              <span>
                {tx.inbox.age(entry.ageDays)}
                {entry.overdue && <strong> · {de.review.overdue}</strong>}
              </span>
            </span>
            <span>
              <Icon name="camera" size={16} />
              {/* With the club's photo rule a report without one is on its way from a phone (media.ts). */}
              {d.imageCount === 0 && settings.data?.mediaRequired ? <strong>{de.media.pending}</strong> : tx.inbox.photos(d.imageCount)}
            </span>
            <span className={entry.nearby.length ? 'icard__near' : ''}>
              <Icon name="pin" size={16} />
              {tx.inbox.nearby(entry.nearby.length)}
            </span>
          </div>
        </div>
      </Link>
      <div className="icard__actions" data-tour="inbox-actions">
        <button type="button" className="btn btn--primary" onClick={() => void confirm()}>
          <Icon name="check" />
          {de.review.confirm}
        </button>
        <button type="button" className="btn" onClick={() => onReason('merge')}>
          <Icon name="merge" />
          {de.review.merge}
        </button>
        <button type="button" className="btn btn--danger" onClick={() => onReason('reject')}>
          <Icon name="reject" />
          {de.review.reject}
        </button>
      </div>
    </li>
  );
}

export function InboxScreen() {
  const ref = useDamageRef();
  const inbox = useInbox();
  const write = useWrite();
  const [aircraft, setAircraft] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ entry: InboxEntry; mode: 'reject' | 'merge' } | null>(null);
  const entries = inbox.data ?? [];
  const regs = [...new Set(entries.map((e) => e.damage.registration))].sort();
  const shown = aircraft ? entries.filter((e) => e.damage.registration === aircraft) : entries;
  const oldest = entries[0];

  return (
    <div className="page">
      <header className="page__head">
        <HelpLink screen="inbox" className="page__help" />
        <h1>{tx.inbox.title}</h1>
        <p className="muted">{tx.inbox.subtitle}</p>
      </header>
      {inbox.isPending ? (
        <Loading />
      ) : inbox.isError ? (
        <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
      ) : entries.length === 0 ? (
        <p className="empty">
          <Icon name="resolve" size={28} />
          <br />
          {tx.inbox.empty}
        </p>
      ) : (
        <>
          <div className="inbox__stats">
            <div>
              <span className="label">{tx.inbox.statOpen}</span>
              <strong className="mono">{entries.length}</strong>
            </div>
            <div>
              <span className="label">{de.review.overdue}</span>
              <strong className="mono">{entries.filter((e) => e.overdue).length}</strong>
            </div>
            <div>
              <span className="label">{tx.inbox.statOldest}</span>
              <strong>{oldest ? tx.inbox.age(oldest.ageDays) : '–'}</strong>
            </div>
          </div>
          <div className="row" role="group" aria-label={tx.detail.aircraft}>
            <Chip pressed={aircraft === null} onToggle={() => setAircraft(null)}>
              {tx.inbox.allAircraft}
            </Chip>
            {regs.map((r) => (
              <Chip key={r} pressed={aircraft === r} onToggle={() => setAircraft(aircraft === r ? null : r)}>
                <span className="mono">{r}</span>
              </Chip>
            ))}
          </div>
          <ul className="inbox" data-tour="inbox-list">
            {shown.map((e) => (
              <InboxCard key={e.damage.id} entry={e} onReason={(mode) => setDialog({ entry: e, mode })} />
            ))}
          </ul>
        </>
      )}
      {dialog && (
        <ReasonDialog
          mode={dialog.mode}
          report={dialog.entry.damage}
          candidates={dialog.entry.nearby.filter((n) => n.status !== 'verworfen')}
          pool={dialog.entry.nearby}
          onCancel={() => setDialog(null)}
          onSubmit={async (input) => {
            const id = dialog.entry.damage.id;
            const action = dialog.mode;
            const r = await write({
              run: (s) => s.reviewDamage(id, { action, ...input }),
              success: (v) => (action === 'merge' && v.mergedIntoNumber ? tx.toast.merged(ref(v.mergedIntoNumber)) : tx.toast.statusChanged(statusLabel.verworfen)),
              undo: (s, res) => s.revertReview(id, res.eventId),
            });
            if (r) setDialog(null);
          }}
        />
      )}
    </div>
  );
}
